import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://subd.checkkonproject.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// In-flight request deduplication & multi-layer cache (Memory L1 + sessionStorage L2)
const pendingRequests = new Map();
const responseCache = new Map();
const DEFAULT_TTL_MS = 2 * 60 * 1000; // 2 minutes default cache for read-heavy lists
const STORAGE_PREFIX = "checkkon_api_cache_v1:";

const normalizeEndpointKey = (url, params = {}) => {
  const cleanUrl = String(url || "")
    .replace(API_BASE_URL, "")
    .replace(/^https?:\/\/[^/]+\/api/, "");
  const normalizedPath = cleanUrl.startsWith("/") ? cleanUrl : `/${cleanUrl}`;
  const paramStr =
    params && Object.keys(params).length > 0 ? JSON.stringify(params) : "{}";
  return `${normalizedPath}_${paramStr}`;
};

const readSessionCache = (cacheKey, ttlMs) => {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(STORAGE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Date.now() - parsed.timestamp < ttlMs) {
      responseCache.set(cacheKey, parsed);
      return parsed.data;
    }
  } catch {
    // Ignore storage errors (e.g. quota exceeded or private mode)
  }
  return null;
};

const writeSessionCache = (cacheKey, entry) => {
  try {
    if (typeof sessionStorage === "undefined") return;
    const serialized = JSON.stringify(entry);
    // Only persist payloads under 250KB in sessionStorage to keep storage fast
    if (serialized.length < 250000) {
      sessionStorage.setItem(STORAGE_PREFIX + cacheKey, serialized);
    }
  } catch {
    // Ignore storage quota errors
  }
};

// Request Interceptor: Attach JWT token automatically
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Invalidate cache automatically on write operations
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head" && method !== "options") {
      invalidateApiCache();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Also intercept global axios mutations so raw axios.post / axios.put / axios.delete calls invalidate cache
axios.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head" && method !== "options") {
      invalidateApiCache();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Centralized error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn("Unauthorized API access - token may be expired");
    }
    return Promise.reject(error);
  }
);

/**
 * Perform a cached GET request with automatic in-flight deduplication.
 * @param {string} url - API endpoint
 * @param {object} config - Axios request config
 * @param {number} ttlMs - Time to live in milliseconds
 */
export const cachedGet = async (url, config = {}, ttlMs = DEFAULT_TTL_MS) => {
  const cacheKey = normalizeEndpointKey(url, config.params || {});

  // 1. Check if fresh L1 memory cached response exists
  const cached = responseCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < ttlMs) {
    return cached.data;
  }

  // 2. Check L2 sessionStorage cache (skip during unit tests to keep test isolation strict)
  if (process.env.NODE_ENV !== "test") {
    const sessionData = readSessionCache(cacheKey, ttlMs);
    if (sessionData !== null) {
      return sessionData;
    }
  }

  // 3. Check if identical request is already in flight (deduplicate)
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // 4. Dispatch new request and register promise
  const requestPromise = api
    .get(url, config)
    .then((res) => {
      const entry = { data: res.data, timestamp: Date.now() };
      responseCache.set(cacheKey, entry);
      if (process.env.NODE_ENV !== "test") {
        writeSessionCache(cacheKey, entry);
      }
      pendingRequests.delete(cacheKey);
      return res.data;
    })
    .catch((err) => {
      pendingRequests.delete(cacheKey);
      throw err;
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

/**
 * Look up a single item from an already-cached list endpoint (e.g. /News_request -> id)
 * Enables 0ms instant rendering when navigating from a list page to a detail view.
 */
export const getCachedListItem = (detailEndpoint, id) => {
  const listEndpointMap = {
    News_show: "/News_request",
    Article_show: "/Article_request",
    MdShare_show: "/MdShare_request",
  };
  const listPath = listEndpointMap[detailEndpoint];
  if (!listPath) return null;

  const listKey = normalizeEndpointKey(listPath, {});
  const cached =
    responseCache.get(listKey)?.data ||
    (process.env.NODE_ENV !== "test"
      ? readSessionCache(listKey, DEFAULT_TTL_MS * 5)
      : null);

  if (Array.isArray(cached)) {
    return (
      cached.find((item) => String(item.id) === String(id)) || null
    );
  }
  return null;
};

/**
 * Invalidate cache for a specific endpoint or clear all
 */
export const invalidateApiCache = (keyPrefix) => {
  if (!keyPrefix) {
    responseCache.clear();
    try {
      if (typeof sessionStorage !== "undefined") {
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith(STORAGE_PREFIX)) {
            sessionStorage.removeItem(k);
          }
        });
      }
    } catch {
      // Ignore storage errors
    }
    return;
  }
  const normalizedPrefix = keyPrefix.startsWith("/")
    ? keyPrefix
    : `/${keyPrefix}`;
  for (const key of responseCache.keys()) {
    if (key.startsWith(normalizedPrefix) || key.startsWith(keyPrefix)) {
      responseCache.delete(key);
    }
  }
  try {
    if (typeof sessionStorage !== "undefined") {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith(STORAGE_PREFIX)) {
          const rawKey = k.slice(STORAGE_PREFIX.length);
          if (
            rawKey.startsWith(normalizedPrefix) ||
            rawKey.startsWith(keyPrefix)
          ) {
            sessionStorage.removeItem(k);
          }
        }
      });
    }
  } catch {
    // Ignore storage errors
  }
};

export const invalidateCache = invalidateApiCache;

/**
 * Transparently deduplicate & cache native window.fetch GET calls to the backend API.
 * Accelerates all legacy/Admin components that call fetch("https://subd.checkkonproject.com/api/...") directly.
 */
let fetchInterceptorInstalled = false;
export const setupGlobalFetchCache = () => {
  if (
    fetchInterceptorInstalled ||
    typeof window === "undefined" ||
    typeof window.fetch !== "function" ||
    process.env.NODE_ENV === "test"
  ) {
    return;
  }
  fetchInterceptorInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  const inFlightFetch = new Map();

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const method = (init?.method || "GET").toUpperCase();

    // Only intercept calls to our backend API
    if (!url.includes("checkkonproject.com/api/")) {
      return nativeFetch(input, init);
    }

    // Any mutation (POST/PUT/DELETE) invalidates cached GET responses immediately
    if (method !== "GET") {
      invalidateApiCache();
      return nativeFetch(input, init);
    }

    const authHeader =
      init?.headers?.Authorization || init?.headers?.authorization || "";
    const cacheKey = normalizeEndpointKey(
      url,
      authHeader ? { auth: authHeader.slice(-16) } : {}
    );

    const ttlMs = url.includes("/user") ? 60000 : DEFAULT_TTL_MS;
    const cached = responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ttlMs) {
      return new Response(JSON.stringify(cached.data), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }

    const sessionData = readSessionCache(cacheKey, ttlMs);
    if (sessionData !== null) {
      return new Response(JSON.stringify(sessionData), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }

    if (inFlightFetch.has(cacheKey)) {
      try {
        const sharedData = await inFlightFetch.get(cacheKey);
        return new Response(JSON.stringify(sharedData), {
          status: 200,
          headers: { "Content-Type": "application/json" },
        });
      } catch {
        return nativeFetch(input, init);
      }
    }

    const fetchPromise = (async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const cloned = response.clone();
      const data = await cloned.json();
      const entry = { data, timestamp: Date.now() };
      responseCache.set(cacheKey, entry);
      writeSessionCache(cacheKey, entry);
      return data;
    })();

    inFlightFetch.set(cacheKey, fetchPromise);

    try {
      const data = await fetchPromise;
      inFlightFetch.delete(cacheKey);
      return new Response(JSON.stringify(data), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      inFlightFetch.delete(cacheKey);
      return nativeFetch(input, init);
    }
  };
};

setupGlobalFetchCache();

export default api;
